import React, { useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	INLINE_DIALOG,
} from '@atlassian/jira-product-analytics-bridge';
import {
	CHART_RIGHT_SPACE_LEFT_PADDING,
	CHART_RIGHT_SPACE_WIDTH,
} from '../../common/constants.tsx';
import { CHART_HEIGHT } from '../../common/ui/line-chart-by-environment/constants.tsx';
import { useSelectedWeekStore } from '../../controllers/selected-date-range/index.tsx';
import { useReportCoreData } from '../../services/deployment-frequency-core/index.tsx';
import {
	isDataAllUnmapped,
	isDataUnmappedFoundWithMissingProd,
} from '../../services/deployment-frequency-core/utils.tsx';
import { Chart } from './chart/index.tsx';
import { convertReportCoreToChartDataPoints } from './chart/utils.tsx';
import { Legend } from './legend/index.tsx';
import { LoadingSkeleton } from './loading-skeleton/index.tsx';
import messages from './messages.tsx';

const Title = () => {
	const { formatMessage } = useIntl();

	return (
		<ChartTitleContainer>
			<Heading size="xsmall">
				{formatMessage(messages.chartTitle)}
				<Tooltip content={formatMessage(messages.titleHelpTooltip)} position="right">
					<QuestionIcon>
						<QuestionCircleIcon size="small" label="" />
					</QuestionIcon>
				</Tooltip>
			</Heading>
		</ChartTitleContainer>
	);
};

const ExpandableContainer = ({ children }: { children: ReactNode }) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<>
			{isOpen ? children : null}
			<Button
				appearance="subtle"
				iconAfter={isOpen ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
				onClick={() => {
					setIsOpen(!isOpen);
				}}
			>
				{isOpen
					? formatMessage(messages.dropDownButtonOpenDescription)
					: formatMessage(messages.dropDownButtonDescription)}
			</Button>
		</>
	);
};

export const LineChartByEnvironment = () => {
	const { formatMessage } = useIntl();
	const { data, loading, error } = useReportCoreData();
	const [{ selectedWeekStart }] = useSelectedWeekStore();

	if (error && data == null) {
		// When error happens, retry button will be displayed by the bar chart, but this chart will display loading skeleton as per the design spec
		return <LoadingSkeleton />;
	}
	if (loading || data == null || selectedWeekStart === null) {
		return <LoadingSkeleton />;
	}
	if (error) {
		// TODO - We would want to fire analytics? Normal data exists but error exist together?!
	}

	const lineSeries = convertReportCoreToChartDataPoints(data);

	return (
		<ExpandableContainer>
			<Title />
			{(isDataAllUnmapped(data) || isDataUnmappedFoundWithMissingProd(data)) && (
				<ContextualAnalyticsData sourceType={INLINE_DIALOG} sourceName="unmappedDeploymentsMessage">
					<Box xcss={noProdContainerStyles}>
						{isDataAllUnmapped(data) ? (
							<SectionMessage
								title={formatMessage(messages.noMappedWarningTitle)}
								appearance="discovery"
							>
								{formatMessage(messages.noMappedWarningDescription)}
							</SectionMessage>
						) : (
							<SectionMessage
								title={formatMessage(messages.nonProdWarningTitle)}
								appearance="discovery"
							>
								{formatMessage(messages.nonProdWarningDescription)}
							</SectionMessage>
						)}
						<FireScreenAnalytics />
					</Box>
				</ContextualAnalyticsData>
			)}
			<ChartContainer>
				<ChartItem>
					<Chart series={lineSeries} />
				</ChartItem>
				<ChartLegend>
					<Legend deploymentReportData={data} selectedWeekStart={selectedWeekStart} />
				</ChartLegend>
			</ChartContainer>
		</ExpandableContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartTitleContainer = styled.div({
	height: token('space.300', '24px'),
	display: 'flex',
	alignItems: 'center',
	margin: `0 0 ${token('space.100', '8px')} 0`,
	paddingLeft: token('space.250', '20px'),
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const QuestionIcon = styled.div({
	marginLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N80),
	cursor: 'pointer',
	paddingTop: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${CHART_HEIGHT}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartItem = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${CHART_RIGHT_SPACE_WIDTH}px)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartLegend = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${CHART_RIGHT_SPACE_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${CHART_RIGHT_SPACE_WIDTH}px`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${CHART_RIGHT_SPACE_LEFT_PADDING}px`,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'start',
});

const noProdContainerStyles = xcss({
	marginBottom: 'space.200',
	paddingLeft: 'space.200',
});
